"use client";

import React, { useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  LayoutDashboard,
  Compass,
  Target,
  HeartPulse,
  ShieldAlert,
  FolderGit,
  CheckSquare,
  Clock,
  FileText,
  UserPlus,
  Building,
  Award,
  Landmark,
  Receipt,
  PiggyBank,
  Users,
  Network,
  UserCheck,
  CalendarDays,
  Trophy,
  Truck,
  Package,
  Lightbulb,
  ChevronLeft,
  Menu,
  Gavel,
  Flame,
  History,
  Newspaper,
  FlaskConical,
  Tv,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface NavigationGroup {
  title: string;
  items: NavigationItem[];
}

const navigationGroups: NavigationGroup[] = [
  {
    title: "Strategy",
    items: [
      { name: "Goals", href: "/strategy/goals", icon: Target },
      { name: "Company Health", href: "/strategy/swot", icon: HeartPulse },
      { name: "Risks", href: "/strategy/risks", icon: ShieldAlert },
      { name: "Decisions", href: "/strategy/decisions", icon: Gavel },
      {
        name: "What-If Sandbox",
        href: "/strategy/sandbox",
        icon: FlaskConical,
      },
      { name: "Daily Pulse", href: "/strategy/daily-pulse", icon: Newspaper },
      { name: "War Room", href: "/strategy/war-room", icon: Tv },
    ],
  },
  {
    title: "Work",
    items: [
      { name: "Projects", href: "/work/projects", icon: FolderGit },
      { name: "Tasks & Sprints", href: "/work/tasks", icon: CheckSquare },
      { name: "Timesheets", href: "/work/timesheets", icon: Clock },
      { name: "Documents", href: "/work/documents", icon: FileText },
    ],
  },
  {
    title: "Sales",
    items: [
      { name: "Leads", href: "/sales/leads", icon: UserPlus },
      { name: "Clients", href: "/sales/clients", icon: Building },
      { name: "Proposals & Contracts", href: "/sales/proposals", icon: Award },
    ],
  },
  {
    title: "Finance",
    items: [
      { name: "Overview", href: "/finance/overview", icon: Landmark },
      { name: "Invoices", href: "/finance/invoices", icon: Receipt },
      { name: "Costs & Expenses", href: "/finance/costs", icon: PiggyBank },
    ],
  },
  {
    title: "People",
    items: [
      { name: "People", href: "/people", icon: Users },
      { name: "Teams", href: "/teams", icon: Network },
      {
        name: "Capacity & Workloads",
        href: "/people/capacity",
        icon: UserCheck,
      },
      { name: "Time Off", href: "/people/time-off", icon: CalendarDays },
      { name: "Growth & Kudos", href: "/people/kudos", icon: Trophy },
      { name: "Burnout Heatmeter", href: "/people/burnout", icon: Flame },
    ],
  },
  {
    title: "Operations",
    items: [
      { name: "Suppliers", href: "/operations/suppliers", icon: Truck },
      { name: "Assets & Inventory", href: "/operations/assets", icon: Package },
      { name: "Initiatives", href: "/operations/initiatives", icon: Lightbulb },
      { name: "Semantic Ledger", href: "/operations/ledger", icon: History },
    ],
  },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

export function Sidebar({
  isOpen,
  onClose,
  isCollapsed,
  onToggleCollapse,
}: SidebarProps) {
  const pathname = usePathname();

  const isLinkActive = (href: string) => {
    return pathname === href || pathname.startsWith(href + "/");
  };

  const sidebarContent = (
    <div className="flex flex-col h-full bg-card border-r border-border text-foreground select-none">
      {/* Brand logo header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border/50">
        <Link
          href="/"
          className="flex items-center gap-2 group outline-none"
          onClick={onClose}
        >
          <div className="h-7 w-7 rounded-lg bg-primary-600 text-text-on-primary flex items-center justify-center font-bold tracking-tight shadow-sm group-hover:bg-primary-700 transition-colors">
            C
          </div>
          {!isCollapsed && (
            <span className="font-bold tracking-tight text-text-primary text-lg">
              Cleos
            </span>
          )}
        </Link>
        <button
          onClick={onToggleCollapse}
          className="hidden md:flex h-6 w-6 rounded-md hover:bg-card-muted text-text-secondary items-center justify-center outline-none border border-border/50 cursor-pointer"
        >
          <ChevronLeft
            className={cn(
              "h-4 w-4 transition-transform",
              isCollapsed && "rotate-180",
            )}
          />
        </button>
      </div>

      {/* Main Navigation Scroll Area */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-6 scrollbar-thin">
        {/* Core items (Overview & Explore) */}
        <div className="space-y-1">
          <Link
            href="/"
            onClick={onClose}
            className={cn(
              "flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors outline-none",
              isLinkActive("/") && pathname === "/"
                ? "bg-primary-50 text-primary-600 font-semibold"
                : "text-text-secondary hover:bg-card-muted hover:text-text-primary",
            )}
          >
            <LayoutDashboard className="h-4 w-4 shrink-0" />
            {!isCollapsed && <span>Overview</span>}
          </Link>
          <Link
            href="/explore"
            onClick={onClose}
            className={cn(
              "flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors outline-none",
              isLinkActive("/explore")
                ? "bg-primary-50 text-primary-600 font-semibold"
                : "text-text-secondary hover:bg-card-muted hover:text-text-primary",
            )}
          >
            <Compass className="h-4 w-4 shrink-0" />
            {!isCollapsed && <span>Explore Graph</span>}
          </Link>
        </div>

        {/* Grouped items */}
        {navigationGroups.map((group) => (
          <div key={group.title} className="space-y-1.5">
            {!isCollapsed && (
              <h3 className="px-3 text-[10px] font-bold tracking-wider text-text-muted uppercase">
                {group.title}
              </h3>
            )}
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const Icon = item.icon;
                const active = isLinkActive(item.href);
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={onClose}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors outline-none",
                      active
                        ? "bg-primary-50 text-primary-600 font-semibold border-l-2 border-primary-600 rounded-l-none"
                        : "text-text-secondary hover:bg-card-muted hover:text-text-primary",
                    )}
                    title={isCollapsed ? item.name : undefined}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    {!isCollapsed && (
                      <span className="truncate">{item.name}</span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar wrapper */}
      <aside
        className={cn(
          "hidden md:block h-full transition-all duration-300 shrink-0",
          isCollapsed ? "w-16" : "w-64",
        )}
      >
        {sidebarContent}
      </aside>

      {/* Mobile Drawer (off-canvas) wrapper */}
      <div
        className={cn(
          "fixed inset-0 z-40 bg-black/30 backdrop-blur-xs transition-opacity duration-300 md:hidden",
          isOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none",
        )}
        onClick={onClose}
      />
      <aside
        className={cn(
          "fixed top-0 bottom-0 left-0 z-50 w-64 h-full bg-card shadow-lg transition-transform duration-300 ease-out md:hidden",
          isOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        {sidebarContent}
      </aside>
    </>
  );
}
